import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Form, Input, Select, Radio, Cascader, Divider, Row, Col, message, Tooltip, AutoComplete } from 'antd';
import type { DefaultOptionType } from 'antd/es/cascader';
import type { RefSelectProps } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { pinyin } from 'pinyin-pro';

import apiClient from '@/api/client';
import { getFieldOptions, createMountain, getProject, getMountainVehicleTypes, getSoftwareSuppliers } from '@/api/config';
import { listBswProjects, type BswProject } from '@/api/requirementSync';
import { useConfigStore } from '@/store/configStore';
import { useAuthStore } from '@/store/authStore';
import { getThirdPartyPlugins } from '@/api/thirdPartyUpload';
import type { Project, FieldOptions, ThirdPartyPlugin, VehicleTypeOption, VehicleOccupier, SoftwareSupplier } from '@/types';
import SortableFileTypeSelect from '@/components/SortableFileTypeSelect';
import SoftwareSupplierSelect from '@/components/SoftwareSupplierSelect';
import LdapUserSelect, { type UserOption } from '@/components/users/LdapUserSelect';

interface ProjectFormModalProps {
  open: boolean;
  project?: Project | null;
  defaultMainline?: boolean;
  onSuccess: () => void;
  onCancel: () => void;
}

interface CreatableSelectProps {
  value?: string;
  onChange?: (v: string) => void;
  options: string[];
  placeholder?: string;
}

const CreatableSelect = ({ value, onChange, options, placeholder }: CreatableSelectProps) => {
  const selectRef = useRef<RefSelectProps>(null);
  return (
    <Select
      ref={selectRef}
      mode="tags"
      allowClear
      maxCount={1}
      placeholder={placeholder}
      value={value ? [value] : []}
      onChange={(vals: string[]) => onChange?.(vals[vals.length - 1] ?? '')}
      onSelect={() => selectRef.current?.blur()}
      options={options.map((o) => ({ value: o, label: o }))}
    />
  );
};

// ── 基础项目层级联动选择器工具函数 ──
function buildProjectCascaderOptions(projects: BswProject[]) {
  const mainlineProjects = projects.filter(p => p.isMainline);
  const normalProjects = projects.filter(p => !p.isMainline);
  const mountainMap = new Map<string, BswProject[]>();
  normalProjects.forEach(p => {
    const key = p.mountain ?? '未分配';
    if (!mountainMap.has(key)) mountainMap.set(key, []);
    mountainMap.get(key)!.push(p);
  });
  return [
    {
      label: '主线',
      value: '__mainline__',
      children: mainlineProjects.map(p => ({ label: p.project, value: String(p.id) })),
    },
    {
      label: '项目',
      value: '__projects__',
      children: [...mountainMap.entries()].map(([mountain, projs]) => ({
        label: mountain,
        value: `__mtn__${mountain}`,
        children: projs.map(p => ({ label: p.project, value: String(p.id) })),
      })),
    },
  ];
}

function projectIdToCascaderValue(id: number, projects: BswProject[]): string[] | undefined {
  const p = projects.find(proj => proj.id === id);
  if (!p) return undefined;
  if (p.isMainline) return ['__mainline__', String(id)];
  return ['__projects__', `__mtn__${p.mountain ?? '未分配'}`, String(id)];
}

const projectCascaderFilter = (input: string, path: DefaultOptionType[]) =>
  path.some(opt => String(opt.label).toLowerCase().includes(input.toLowerCase()));

const emptyFieldOptions: FieldOptions = {
  adcuModels: [], mcuModels: [], platforms: [], oses: [], socMiddlewares: [], mcuMiddlewares: [],
};

// ── 子车型选择值用「车型ID:子车型名」复合键：不同车型下可能存在同名子车型，
// 只用子车型名做 value 会互相覆盖。
const SUBTYPE_KEY_SEP = ':';

const toSubtypeKey = (vehicleTypeId: number, vehicleSubtype: string) =>
  `${vehicleTypeId}${SUBTYPE_KEY_SEP}${vehicleSubtype}`;

const parseSubtypeKey = (key: string) => {
  const sep = key.indexOf(SUBTYPE_KEY_SEP);
  return {
    vehicleTypeId: Number(key.slice(0, sep)),
    vehicleSubtype: key.slice(sep + 1),
  };
};

// 老车型（来自 VE 老车型接口）在名称后加后缀，便于和谱系接口的车型区分。
// 仅用于显示，落库的 vehicle_type 始终是原始名称。
const YAML_SUFFIX = ' (yaml)';

const vehicleTypeLabel = (option: VehicleTypeOption) =>
  `${option.vehicle_type || String(option.vehicle_type_id)}`
  + `${option.source === 'yaml' ? YAML_SUFFIX : ''}`;

const ProjectFormModal = ({ open, project, defaultMainline, onSuccess, onCancel }: ProjectFormModalProps) => {
  const [form] = Form.useForm();
  const { fileTypes, mountains, projects, rpaProjectOptions, fetchFileTypes, fetchMountains, fetchRpaProjectOptions } = useConfigStore();
  const { user } = useAuthStore();
  const [fieldOptions, setFieldOptions] = useState<FieldOptions>(emptyFieldOptions);
  const [allProjects, setAllProjects] = useState<BswProject[]>([]);
  const [submitting, setSubmitting] = useState(false);
  // 仅取可用插件：已下架的插件后端会拒绝保存，不该出现在下拉里
  const [thirdPartyPlugins, setThirdPartyPlugins] = useState<ThirdPartyPlugin[]>([]);
  const [softwareSuppliers, setSoftwareSuppliers] = useState<SoftwareSupplier[]>([]);
  // 新建时用 local state 追踪已选基础项目 ID（Form.useWatch 对未注册字段无响应）
  const [selectedBaseProjectId, setSelectedBaseProjectId] = useState<number | undefined>(undefined);
  const reqMode = Form.useWatch('req_mode', form) as string | undefined;
  const isMainline = Form.useWatch('is_mainline', form) as boolean | undefined;
  const allProjectsCascaderOptions = useMemo(() =>
    buildProjectCascaderOptions(allProjects.filter(p => !project || p.id !== project.id)),
    [allProjects, project]);
  // 编辑模式直接用 project.req_project_id；新建模式用 local state
  const effectiveBaseProjectId = project ? (project.req_project_id ?? undefined) : selectedBaseProjectId;
  const baseCascaderVal = useMemo(
    () => effectiveBaseProjectId != null ? projectIdToCascaderValue(effectiveBaseProjectId, allProjects) : undefined,
    [effectiveBaseProjectId, allProjects]);
  const nameWatch = Form.useWatch('name', form) as string | undefined;
  const mountainNameWatch = Form.useWatch('mountainName', form) as string | undefined;
  const vehicleTypeIdsWatch = Form.useWatch('vehicle_type_ids', form) as number[] | undefined;
  const vehicleSubtypeIdsWatch = Form.useWatch('vehicle_subtype_ids', form) as string[] | undefined;

  // 选中山头是否已确定 RPA 项目空间 Key：山头一旦配置，项目侧即自动继承，无需再展示该字段
  const selectedMountain = useMemo(
    () =>
      mountainNameWatch
        ? mountains.find((m) => m.name.toLowerCase() === mountainNameWatch.trim().toLowerCase())
        : undefined,
    [mountainNameWatch, mountains],
  );
  const mountainHasRpaKey = !!selectedMountain?.project_key;

  // 「RPA 项目空间Key」候选项：来自 RPA /project-map，label 附带简名与已占用山头，便于人工核对匹配
  const rpaKeyOptions = useMemo(
    () =>
      rpaProjectOptions.map((o) => ({
        value: o.project_key,
        label: o.bsp_mountains.length
          ? `${o.short_name || '(未命名)'} · ${o.project_key}（已用：${o.bsp_mountains.join('、')}）`
          : `${o.short_name || '(未命名)'} · ${o.project_key}`,
        searchText: [o.short_name, o.project_key, ...o.bsp_mountains]
          .filter(Boolean)
          .join(' ')
          .toLowerCase(),
      })),
    [rpaProjectOptions],
  );

  // New state for UX enhancements
  const [nameWarning, setNameWarning] = useState('');
  const [mountainOpen, setMountainOpen] = useState(false);
  const [mountainSearch, setMountainSearch] = useState('');
  // 车型：选项来自 VE（按所属山头的英文名查询），已被其他项目占用的项置灰
  const [vehicleTypeOptions, setVehicleTypeOptions] = useState<VehicleTypeOption[]>([]);
  const [vehicleTypesLoading, setVehicleTypesLoading] = useState(false);
  const [vehicleTypesLoaded, setVehicleTypesLoaded] = useState(false);
  const [vehicleTypesError, setVehicleTypesError] = useState('');
  // 「更多...」：按需补齐 VE 老车型接口里才有的老车型（谱系接口查不到）
  const [moreVehicleTypesLoading, setMoreVehicleTypesLoading] = useState(false);
  const [moreVehicleTypesLoaded, setMoreVehicleTypesLoaded] = useState(false);
  // 记录候选列表当前对应的山头，用于丢弃切换山头后迟到的「更多」响应
  const vehicleTypesMountainRef = useRef('');
  // 记录「更多」已为哪个山头加载过，避免重复请求与重复提示
  const moreLoadedMountainRef = useRef('');
  // Track whether user manually edited the code field (stops auto-generation if true)
  const codeManuallyEdited = useRef(false);

  // 子车型候选：已选车型下包含的子车型，按车型分组。值用复合键避免跨车型重名互相覆盖。
  // 已选中但不在候选项里的值补一条，保证标签始终可读（如编辑预填时接口尚未返回）。
  // 被其他项目占用的项照常可选，仅在 label 与下方 extra 里标明占用方，由后端在提交时定夺。
  const vehicleSubtypeOptions = useMemo(() => {
    const selected = new Set(vehicleTypeIdsWatch || []);
    const groups = vehicleTypeOptions
      .filter((v) => selected.has(v.vehicle_type_id))
      .map((v) => ({
        label: vehicleTypeLabel(v),
        options: (v.subtypes || []).map((s) => ({
          value: toSubtypeKey(v.vehicle_type_id, s.vehicle_subtype),
          label: s.occupied_by_projects.length
            ? `${s.vehicle_subtype}（已被「${s.occupied_by_projects[0].name}」占用）`
            : s.vehicle_subtype,
        })),
      }))
      .filter((g) => g.options.length > 0);
    const known = new Set(groups.flatMap((g) => g.options.map((o) => o.value)));
    const missing = (vehicleSubtypeIdsWatch || [])
      .filter((key) => !known.has(key))
      .map((key) => ({ value: key, label: parseSubtypeKey(key).vehicleSubtype }));
    return [...groups, ...missing];
  }, [vehicleTypeOptions, vehicleTypeIdsWatch, vehicleSubtypeIdsWatch]);

  // 车型下拉选项：占用提示拼在名称后，老车型（yaml）再补 (yaml) 后缀
  const vehicleTypeSelectOptions = useMemo(() => vehicleTypeOptions.map((v) => ({
    value: v.vehicle_type_id,
    label: v.occupied_by_projects.length
      ? `${vehicleTypeLabel(v)}（已被「${v.occupied_by_projects[0].name}」占用）`
      : vehicleTypeLabel(v),
  })), [vehicleTypeOptions]);

  // 已选中但已被其他项目使用的车型/子车型：列出占用方（含其硬件平台）供人工核对
  const occupiedHints = useMemo(() => {
    const describe = (holders: VehicleOccupier[]) =>
      holders
        .map((h) => (h.platform ? `「${h.name}」（硬件平台：${h.platform}）` : `「${h.name}」`))
        .join('、');
    const vehicleHints = (vehicleTypeIdsWatch || []).flatMap((id) => {
      const option = vehicleTypeOptions.find((v) => v.vehicle_type_id === id);
      return option?.occupied_by_projects.length
        ? [`「${option.vehicle_type}」已被项目 ${describe(option.occupied_by_projects)} 使用`]
        : [];
    });
    const subtypeHints = (vehicleSubtypeIdsWatch || []).flatMap((key) => {
      const { vehicleTypeId, vehicleSubtype } = parseSubtypeKey(key);
      const holders = vehicleTypeOptions
        .find((v) => v.vehicle_type_id === vehicleTypeId)
        ?.subtypes.find((s) => s.vehicle_subtype === vehicleSubtype)
        ?.occupied_by_projects;
      return holders?.length
        ? [`「${vehicleSubtype}」已被项目 ${describe(holders)} 使用`]
        : [];
    });
    return { vehicleHints, subtypeHints };
  }, [vehicleTypeOptions, vehicleTypeIdsWatch, vehicleSubtypeIdsWatch]);

  // 车型取消勾选后，其下的子车型选择不再适用，自动剔除
  const pruneVehicleSubtypes = useCallback((keptVehicleIds: number[]) => {
    const kept = ((form.getFieldValue('vehicle_subtype_ids') as string[] | undefined) || [])
      .filter((key) => keptVehicleIds.includes(parseSubtypeKey(key).vehicleTypeId));
    form.setFieldValue('vehicle_subtype_ids', kept);
  }, [form]);

  // Convert a single string to a safe code segment: Chinese → pinyin initials, ASCII → trimmed
  const toCodeSegment = useCallback((val: string): string => {
    const trimmed = (val || '').trim();
    if (!trimmed) return '';
    const hasChinese = /[一-鿿぀-ヿ가-힯]/.test(trimmed);
    if (hasChinese) {
      return pinyin(trimmed, { pattern: 'first', toneType: 'none', nonZh: 'consecutive' })
        .replace(/\s+/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');
    }
    return trimmed.replace(/\s+/g, '');
  }, []);

  // Generate unique code from 所属山头 + 项目名称, format: "mountain_name"
  const generateUniqueCode = useCallback((name: string, mountainName: string) => {
    const namePart = toCodeSegment(name);
    const mountainPart = toCodeSegment(mountainName);
    let base = mountainPart ? `${mountainPart}_${namePart}` : namePart;
    if (!base) base = 'project';
    const existingCodes = new Set(projects.map((p) => (p.code || '').toLowerCase()));
    if (!existingCodes.has(base.toLowerCase())) return base;
    for (let i = 2; i <= 99; i++) {
      const cand = `${base}${i}`;
      if (!existingCodes.has(cand.toLowerCase())) return cand;
    }
    return base;
  }, [projects, toCodeSegment]);

  // Regenerate code whenever name or mountainName changes (for new projects only)
  useEffect(() => {
    if (!open || project) return;
    if (codeManuallyEdited.current) return;
    if (!nameWatch?.trim()) return;
    form.setFieldValue('code', generateUniqueCode(nameWatch, mountainNameWatch || ''));
  }, [nameWatch, mountainNameWatch, open, project, generateUniqueCode, form]);

  // RPA 项目空间 Key 由所属山头自动填充（取山头 project_key）。
  // 山头已配置 Key 时由山头统一下发；山头未配置时保留项目自身已填写的值，不覆盖。
  useEffect(() => {
    if (!open) return;
    const name = (mountainNameWatch ?? '').trim();
    const mtn = name
      ? mountains.find((m) => m.name.toLowerCase() === name.toLowerCase())
      : undefined;
    if (mtn?.project_key) {
      form.setFieldValue('rpa_project_key', mtn.project_key);
    }
  }, [mountainNameWatch, mountains, open, form]);

  // 「更多...」：从 VE 老车型接口补齐谱系接口查不到的老车型，按车型 id 去重后并入候选。
  // 两个数据源的车型 id 是同一套，且谱系集合恒为老车型集合的子集，故按 id 去重即可。
  // baseOptions 由调用方传入当前候选（编辑态回显时传刚落地的谱系结果），避免读到未提交的 state。
  const handleLoadMoreVehicleTypes = useCallback(async (baseOptions: VehicleTypeOption[]) => {
    const name = (mountainNameWatch ?? '').trim();
    if (!name) return;
    setMoreVehicleTypesLoading(true);
    try {
      const res = await getMountainVehicleTypes(name, project?.id, 'yaml');
      // 山头已切换：本次结果属于旧山头，丢弃
      if (vehicleTypesMountainRef.current !== name) return;
      const existing = new Set(baseOptions.map((v) => v.vehicle_type_id));
      const extra = res.vehicle_types
        .filter((v) => !existing.has(v.vehicle_type_id))
        .map((v) => ({ ...v, source: 'yaml' as const }));
      setVehicleTypeOptions((prev) => {
        const known = new Set(prev.map((v) => v.vehicle_type_id));
        return [...prev, ...extra.filter((v) => !known.has(v.vehicle_type_id))];
      });
      setMoreVehicleTypesLoaded(true);
      moreLoadedMountainRef.current = name;
      if (!extra.length) message.info('没有更多车型了');
    } catch {
      message.error('获取更多车型失败，请稍后重试');
    } finally {
      setMoreVehicleTypesLoading(false);
    }
  }, [mountainNameWatch, project]);

  // 车型候选：山头中文名 → VE 英文名 → 车型列表。主线没有真实山头，跳过。
  // 占用信息与硬件平台解耦（后端不按平台过滤），故切换平台无需重新拉取。
  // 请求失败时清空已选车型，确保不把无效/陈旧数据写库（其余字段仍可正常保存）。
  useEffect(() => {
    if (!open || isMainline) return;
    const name = (mountainNameWatch ?? '').trim();
    // 换山头时旧山头的候选（含老车型）一律作废，避免残留到新山头
    if (vehicleTypesMountainRef.current !== name) {
      vehicleTypesMountainRef.current = name;
      moreLoadedMountainRef.current = '';
      setVehicleTypeOptions([]);
      setMoreVehicleTypesLoaded(false);
      setMoreVehicleTypesLoading(false);
    }
    if (!name) {
      setVehicleTypesError('');
      setVehicleTypesLoaded(false);
      return;
    }
    let cancelled = false;
    setVehicleTypesLoading(true);
    setVehicleTypesError('');
    setVehicleTypesLoaded(false);
    getMountainVehicleTypes(name, project?.id)
      .then((res) => {
        if (cancelled) return;
        // 保留已并入的老车型：谱系请求在途时用户可能已点过「更多...」，
        // 直接覆盖会让老车型消失且底部行已置灰，用户无从重试。
        setVehicleTypeOptions((prev) => [
          ...res.vehicle_types,
          ...prev.filter((v) => v.source === 'yaml'),
        ]);
        setVehicleTypesLoaded(true);
        // 项目已保存的车型不在谱系结果里，说明是老车型：自动补拉一次老车型候选，
        // 否则编辑态只能看到裸 id，其子车型也选不了。
        const known = new Set(res.vehicle_types.map((v) => v.vehicle_type_id));
        const savedIds = (project?.vehicle_types || []).map((v) => v.vehicle_type_id);
        if (moreLoadedMountainRef.current !== name
            && savedIds.some((id) => !known.has(id))) {
          void handleLoadMoreVehicleTypes(res.vehicle_types);
        }
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const err = error as { response?: { data?: { message?: string } } };
        setVehicleTypesError(err.response?.data?.message || '获取车型失败，请稍后重试');
        // 刻意不清空已选与候选项：VE 不可用时既不该让用户误以为车型丢了，
        // 也不该在提交时把既有车型抹掉（提交时见 handleFinish 的省略逻辑）。
      })
      .finally(() => {
        if (!cancelled) setVehicleTypesLoading(false);
      });
    return () => { cancelled = true; };
  }, [mountainNameWatch, open, isMainline, project, form, handleLoadMoreVehicleTypes]);

  // Auto-fill file types and notify users: union of all projects under the selected mountain
  const handleMountainAutoFill = useCallback((mountainName: string) => {
    const matchingProjects = projects.filter(
      (p) => p.mountain?.name?.toLowerCase() === mountainName.toLowerCase()
    );
    if (matchingProjects.length === 0) return;

    const seenFt = new Set<number>();
    const allowedFtIds: number[] = [];
    const seenMomFt = new Set<number>();
    const momentaFtIds: number[] = [];
    const seenNotify = new Set<number>();
    const notifyUsers: UserOption[] = [];
    const seenReqNotify = new Set<number>();
    const reqChangeNotifyUsers: UserOption[] = [];
    const seenReqChange = new Set<number>();
    const reqChangeUsers: UserOption[] = [];

    for (const p of matchingProjects) {
      for (const ft of p.allowed_file_types || []) {
        if (!seenFt.has(ft.id)) { seenFt.add(ft.id); allowedFtIds.push(ft.id); }
      }
      for (const ft of p.momenta_file_types || []) {
        if (!seenMomFt.has(ft.id)) { seenMomFt.add(ft.id); momentaFtIds.push(ft.id); }
      }
      for (const u of p.notify_users || []) {
        if (!seenNotify.has(u.id)) { seenNotify.add(u.id); notifyUsers.push({ value: u.id, label: u.username }); }
      }
      for (const u of p.requirement_change_notify_users || []) {
        if (!seenReqNotify.has(u.id)) {
          seenReqNotify.add(u.id);
          reqChangeNotifyUsers.push({ value: u.id, label: u.username });
        }
      }
      for (const u of p.requirement_change_users || []) {
        if (!seenReqChange.has(u.id)) { seenReqChange.add(u.id); reqChangeUsers.push({ value: u.id, label: u.username }); }
      }
    }

    form.setFieldsValue({
      allowed_file_type_ids: allowedFtIds,
      momenta_file_type_ids: momentaFtIds,
      notify_user_ids: notifyUsers,
      requirement_change_notify_user_ids: reqChangeNotifyUsers,
      requirement_change_user_ids: reqChangeUsers,
    });
  }, [form, projects]);

  // Auto-fill technical fields (and more) when base project is selected
  const handleBaseProjectChange = useCallback(async (id: number | undefined) => {
    if (!id) return;
    try {
      const p = await getProject(String(id));
      const techFields = {
        adcu_model: p.adcu_model || undefined,
        mcu_model: p.mcu_model || undefined,
        platform: p.platform || undefined,
        os: p.os || undefined,
        soc_middleware: p.soc_middleware || undefined,
        mcu_middleware: p.mcu_middleware || undefined,
      };
      if (p.is_mainline) {
        // Mainline: fill only the 6 technical fields
        form.setFieldsValue(techFields);
      } else {
        // Non-mainline: fill all fields (skip name and code)
        form.setFieldsValue({
          ...techFields,
          mountainName: p.mountain?.name,
          description: p.description || '',
          opl_required: p.opl_required ?? undefined,
          req_mode: p.req_mode ?? (
            !p.req_project_id ? 'none'
            : p.req_project_id === p.id ? 'independent'
            : 'shared'
          ),
          allowed_file_type_ids: p.allowed_file_types?.map((ft) => ft.id) || [],
          momenta_file_type_ids: p.momenta_file_types?.map((ft) => ft.id) || [],
          owner_id: p.owner_user ? { value: p.owner_user.id, label: p.owner_user.username } : undefined,
          notify_user_ids: p.notify_users?.map((u) => ({ value: u.id, label: u.username })) || [],
          requirement_change_notify_user_ids:
            p.requirement_change_notify_users?.map((u) => ({ value: u.id, label: u.username })) || [],
          requirement_change_user_ids:
            p.requirement_change_users?.map((u) => ({ value: u.id, label: u.username })) || [],
        });
      }
    } catch {
      // ignore
    }
  }, [form]);

  useEffect(() => {
    if (!open) return;

    fetchFileTypes();
    fetchMountains();
    fetchRpaProjectOptions();
    getFieldOptions().then(setFieldOptions).catch(() => setFieldOptions(emptyFieldOptions));
    listBswProjects().then(r => { if (r.code === 200) setAllProjects(r.data); }).catch(() => setAllProjects([]));
    getThirdPartyPlugins(true).then(setThirdPartyPlugins).catch(() => setThirdPartyPlugins([]));
    getSoftwareSuppliers().then(setSoftwareSuppliers).catch(() => setSoftwareSuppliers([]));

    setNameWarning('');
    setSelectedBaseProjectId(undefined);
    codeManuallyEdited.current = false;
    setVehicleTypeOptions([]);
    setVehicleTypesError('');
    setVehicleTypesLoaded(false);

    if (project) {
      form.setFieldsValue({
        name: project.name,
        code: project.code,
        description: project.description || '',
        mountainName: project.mountain?.name,
        is_mainline: project.is_mainline ?? defaultMainline ?? false,
        os: project.os || undefined,
        platform: project.platform || undefined,
        adcu_model: project.adcu_model || undefined,
        mcu_model: project.mcu_model || undefined,
        soc_middleware: project.soc_middleware || undefined,
        mcu_middleware: project.mcu_middleware || undefined,
        req_mode: project.req_mode ?? (
          !project.req_project_id ? 'none'
          : project.req_project_id === project.id ? 'independent'
          : 'shared'
        ),
        opl_required: project.opl_required ?? undefined,
        design_material_required: project.design_material_required ?? undefined,
        third_party_plugin_id: project.third_party_plugin?.id ?? undefined,
        software_supplier_ids: project.software_suppliers?.map((s) => s.id) ?? [],
        baseProjectId: (
          project.req_mode === 'shared'
          || project.req_mode === 'independent'
          || (!project.req_mode && project.req_project_id && project.req_project_id !== project.id)
        )
          ? (project.req_project_id ?? undefined) : undefined,
        allowed_file_type_ids: project.allowed_file_types?.map((ft) => ft.id) || [],
        momenta_file_type_ids: project.momenta_file_types?.map((ft) => ft.id) || [],
        owner_id: project.owner_user ? { value: project.owner_user.id, label: project.owner_user.username } : undefined,
        notify_user_ids: project.notify_users?.map((u) => ({ value: u.id, label: u.username })) || [],
        requirement_change_notify_user_ids:
          project.requirement_change_notify_users?.map((u) => ({ value: u.id, label: u.username })) || [],
        requirement_change_user_ids:
          project.requirement_change_users?.map((u) => ({ value: u.id, label: u.username })) || [],
        rpa_project_key: project.rpa_config?.project_key || '',
        rpa_vehicle_mode: project.rpa_vehicle_mode ?? 'combo',
        rpa_ccb_reviewers: (project.rpa_config?.ccb_reviewers || []).map((name, idx) => ({
          value: -(idx + 1),
          label: name,
        })),
        vehicle_type_ids: [...new Set(project.vehicle_types?.map((v) => v.vehicle_type_id) || [])],
        vehicle_subtype_ids: (project.vehicle_types || [])
          .filter((v) => v.vehicle_subtype)
          .map((v) => toSubtypeKey(v.vehicle_type_id, v.vehicle_subtype)),
      });
      // 先用已保存的车型/子车型垫一份选项，让标签在接口返回前就能渲染
      if (project.vehicle_types?.length) {
        const byTypeId = new Map<number, VehicleTypeOption>();
        for (const v of project.vehicle_types) {
          if (!byTypeId.has(v.vehicle_type_id)) {
            byTypeId.set(v.vehicle_type_id, {
              vehicle_type_id: v.vehicle_type_id,
              vehicle_type: v.vehicle_type,
              occupied_by_projects: [],
              subtypes: [],
            });
          }
          if (v.vehicle_subtype) {
            const option = byTypeId.get(v.vehicle_type_id)!;
            if (!option.subtypes.some((s) => s.vehicle_subtype === v.vehicle_subtype)) {
              option.subtypes.push({
                vehicle_subtype: v.vehicle_subtype,
                occupied_by_projects: [],
              });
            }
          }
        }
        setVehicleTypeOptions([...byTypeId.values()]);
      }
    } else {
      form.resetFields();
      form.setFieldsValue({
        is_mainline: defaultMainline ?? false,
        owner_id: user ? { value: user.id, label: user.username } : undefined,
        opl_required: defaultMainline ? false : true,
        design_material_required: defaultMainline ? false : true,
        req_mode: 'independent',
        rpa_vehicle_mode: 'combo',
        ...(defaultMainline ? { mountainName: '主线' } : {}),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, project]);

  const handleFinish = async (values: Record<string, unknown>) => {
    // Block submission if project name is duplicate
    const nameVal = ((values.name as string) || '').trim();
    const others = projects.filter((p) => !project || p.id !== project.id);
    if (others.some((p) => p.name === nameVal)) {
      Modal.warning({ title: '项目名称重复', content: `项目名称"${nameVal}"已存在，请修改后重试` });
      return;
    }

    setSubmitting(true);
    try {
      let mountainId: number | null = null;
      const mountainName = (values.mountainName as string | undefined)?.trim();
      if (mountainName) {
        const existing = mountains.find((m) => m.name.toLowerCase() === mountainName.toLowerCase());
        if (existing) {
          mountainId = existing.id;
        } else {
          const created = await createMountain({ name: mountainName });
          mountainId = created.id;
        }
      }

      const extractIds = (v: unknown) =>
        ((v as { value: number }[]) || []).map((item) => (typeof item === 'object' ? item.value : item));

      const rpaConfig: Record<string, unknown> = { ...(project?.rpa_config || {}) };
      // 山头已确定 Key 时由后端按山头统一下发，项目侧不再单独提交；
      // 山头未配置时允许在项目侧直接指定（或清空）RPA 项目空间 Key
      if (!mountainHasRpaKey) {
        const rpaProjectKey = (values.rpa_project_key as string)?.trim();
        if (rpaProjectKey) rpaConfig.project_key = rpaProjectKey;
        else delete rpaConfig.project_key;
      }
      // LdapUserSelect 返回 {value: id, label: username}[]，RPA payload 只需要 username 列表
      const ccbReviewers = ((values.rpa_ccb_reviewers as UserOption[]) || [])
        .map((o) => o.label)
        .filter(Boolean);
      if (ccbReviewers.length) rpaConfig.ccb_reviewers = ccbReviewers;
      else delete rpaConfig.ccb_reviewers;

      const ownerOption = values.owner_id as { value: number } | null | undefined;

      // 车型/子车型：由当前选项回查名称；主线不设车型。
      // 若 VE 查询失败则整体省略该字段（undefined 会被 JSON 序列化丢弃），
      // 后端视为「不改动」，从而不会因一次 VE 抖动就把项目既有车型清空。
      const selectedVehicleIds = (values.vehicle_type_ids as number[] | undefined) || [];
      const subtypesByType = new Map<number, string[]>();
      for (const key of (values.vehicle_subtype_ids as string[] | undefined) || []) {
        const { vehicleTypeId, vehicleSubtype } = parseSubtypeKey(key);
        subtypesByType.set(
          vehicleTypeId, [...(subtypesByType.get(vehicleTypeId) || []), vehicleSubtype]);
      }
      const vehicleTypesValue = values.is_mainline
        ? []
        : vehicleTypesError
          ? undefined
          : selectedVehicleIds.flatMap((id) => {
              // 候选里没有时（老车型候选拉取失败）回落到项目已保存的名称，避免写成空串
              const name = vehicleTypeOptions.find((v) => v.vehicle_type_id === id)?.vehicle_type
                || project?.vehicle_types?.find((v) => v.vehicle_type_id === id)?.vehicle_type
                || '';
              // 未选子车型时落一条空子车型记录，表示该车型不区分子车型
              const subtypes = subtypesByType.get(id) || [''];
              return subtypes.map((subtype) => ({
                vehicle_type_id: id,
                vehicle_type: name,
                vehicle_subtype: subtype,
              }));
            });

      const payload = {
        name: values.name,
        code: values.code,
        description: values.description || '',
        is_mainline: values.is_mainline,
        os: values.os,
        platform: values.platform,
        adcu_model: values.adcu_model,
        mcu_model: values.mcu_model,
        soc_middleware: values.soc_middleware,
        mcu_middleware: values.mcu_middleware || '',
        opl_required: values.opl_required,
        design_material_required: values.design_material_required,
        // 清空下拉即停用第三方上传（FR-012）；undefined 需显式转 null 才能落库
        third_party_plugin_id: (values.third_party_plugin_id as number | undefined) ?? null,
        // 留空即未指定供应商；undefined 需显式转空数组才能清空
        software_supplier_ids: (values.software_supplier_ids as number[] | undefined) ?? [],
        req_mode: values.req_mode,
        // independent: pass base project id so backend can copy requirement rows
        // shared: pass base project id as the effective requirement source
        // none: null
        req_project_id: (values.req_mode === 'shared' || values.req_mode === 'independent')
          ? (form.getFieldValue('baseProjectId') ?? null)
          : null,
        mountain_id: mountainId,
        vehicle_types: vehicleTypesValue,
        allowed_file_type_ids: values.allowed_file_type_ids || [],
        momenta_file_type_ids: values.momenta_file_type_ids || [],
        owner_id: ownerOption?.value ?? null,
        notify_user_ids: extractIds(values.notify_user_ids),
        requirement_change_notify_user_ids: extractIds(values.requirement_change_notify_user_ids),
        requirement_change_user_ids: extractIds(values.requirement_change_user_ids),
        rpa_config: rpaConfig,
        rpa_vehicle_mode: values.rpa_vehicle_mode ?? 'combo',
      };

      if (project) {
        await apiClient.put(`/projects/${project.id}/`, payload);
        message.success('更新成功');
      } else {
        await apiClient.post('/projects/', payload);
        message.success('创建成功');
      }
      if (values.is_mainline) {
        window.dispatchEvent(new CustomEvent('mainline-project-changed'));
      }
      onSuccess();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { code?: string[]; name?: string[]; detail?: string; message?: string } } };
      const errorMessage =
        err.response?.data?.message
        || err.response?.data?.code?.[0] || err.response?.data?.name?.[0]
        || err.response?.data?.detail || '操作失败';
      message.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={project ? '编辑项目' : '新建项目'}
      open={open}
      onCancel={onCancel}
      onOk={() => form.submit()}
      confirmLoading={submitting}
      width={960}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" onFinish={handleFinish} style={{ marginTop: 8 }}>
        <Divider titlePlacement="start" style={{ margin: '0 0 10px' }}>项目信息</Divider>

        {/* Row 1: 类型 + 所属山头 + 车型（顺序：山头 → 车型 → 项目名称） */}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="is_mainline" label="类型" rules={[{ required: true, message: '请选择类型' }]} style={{ marginBottom: 10 }}>
              <Radio.Group
                disabled={!!project || defaultMainline !== undefined}
                options={[{ label: '主线', value: true }, { label: '项目', value: false }]}
                onChange={(e) => {
                  if (e.target.value) {
                    form.setFieldsValue({
                      mountainName: '主线',
                      req_mode: 'independent',
                      opl_required: false,
                      design_material_required: false,
                      allowed_file_type_ids: [],
                      momenta_file_type_ids: [],
                      notify_user_ids: [],
                      vehicle_type_ids: [],
                      vehicle_subtype_ids: [],
                    });
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="mountainName" label="所属山头" style={{ marginBottom: 10 }}>
              <Select
                allowClear
                showSearch
                disabled={!!isMainline}
                open={mountainOpen}
                onOpenChange={(v) => {
                  setMountainOpen(v);
                  if (!v) setMountainSearch('');
                }}
                onSearch={setMountainSearch}
                filterOption={false}
                placeholder="选择或输入山头（可选）"
                options={[
                  ...mountains
                    .filter((m) => !mountainSearch || m.name.toLowerCase().includes(mountainSearch.toLowerCase()))
                    .map((m) => ({ value: m.name, label: m.name })),
                  ...(mountainSearch && !mountains.some((m) => m.name.toLowerCase() === mountainSearch.toLowerCase())
                    ? [{ value: mountainSearch, label: `创建"${mountainSearch}"` }]
                    : []),
                ]}
                onChange={() => {
                  // 换山头后原车型/子车型已不适用，清空已选（编辑模式的预填不会触发 onChange）
                  form.setFieldValue('vehicle_type_ids', []);
                  form.setFieldValue('vehicle_subtype_ids', []);
                }}
                onSelect={(val: string) => {
                  setMountainOpen(false);
                  setMountainSearch('');
                  if (!project) {
                    handleMountainAutoFill(val);
                  }
                }}
              />
            </Form.Item>
          </Col>
          {!isMainline && (
            <Col span={8}>
              <Form.Item
                name="vehicle_type_ids"
                label="车型"
                tooltip="占用按「车型 + 子车型 + 硬件平台」判定：同一组合只能归属一个项目。已被其他项目使用的车型仍可勾选，但保存时会被后端拒绝"
                validateStatus={vehicleTypesError ? 'error' : undefined}
                help={
                  vehicleTypesError
                  || (!vehicleTypesLoading && vehicleTypesLoaded && vehicleTypeOptions.length === 0
                    ? '该山头下未查询到车型'
                    : undefined)
                }
                extra={
                  occupiedHints.vehicleHints.length
                    ? (
                      <span style={{ color: '#faad14' }}>
                        {occupiedHints.vehicleHints.join('；')}
                      </span>
                    )
                    : undefined
                }
                style={{ marginBottom: 10 }}
              >
                <Select
                  mode="multiple"
                  allowClear
                  placeholder={mountainNameWatch ? '选择车型（可多选）' : '请先选择所属山头'}
                  disabled={!mountainNameWatch || !!vehicleTypesError}
                  loading={vehicleTypesLoading}
                  options={vehicleTypeSelectOptions}
                  onChange={(ids: number[]) => pruneVehicleSubtypes(ids)}
                  filterOption={(input, option) =>
                    String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  popupRender={(menu) => (
                    <>
                      {menu}
                      <Divider style={{ margin: '4px 0' }} />
                      <div
                        role="button"
                        // 阻止默认行为，否则点击会让多选下拉失焦收起
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          if (!moreVehicleTypesLoaded && !moreVehicleTypesLoading) {
                            void handleLoadMoreVehicleTypes(vehicleTypeOptions);
                          }
                        }}
                        style={{
                          padding: '4px 12px',
                          cursor: moreVehicleTypesLoaded ? 'default' : 'pointer',
                          color: moreVehicleTypesLoaded ? 'rgba(0, 0, 0, 0.25)' : '#1677ff',
                        }}
                      >
                        {moreVehicleTypesLoading
                          ? '加载中…'
                          : (moreVehicleTypesLoaded ? '没有更多车型了' : '更多...')}
                      </div>
                    </>
                  )}
                />
              </Form.Item>
            </Col>
          )}
        </Row>

        {/* Row 2: 子车型 + 项目名称 + 项目代码 */}
        <Row gutter={16}>
          {!isMainline && (
            <Col span={8}>
              <Form.Item
                name="vehicle_subtype_ids"
                label={
                  <span style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                    子车型(不同子车型软件&需求不共版时填写)
                  </span>
                }
                tooltip="选项为已选车型下包含的子车型；留空表示该车型不区分子车型。已被其他项目使用的子车型仍可勾选，但保存时会被后端拒绝"
                extra={
                  occupiedHints.subtypeHints.length
                    ? (
                      <span style={{ color: '#faad14' }}>
                        {occupiedHints.subtypeHints.join('；')}
                      </span>
                    )
                    : undefined
                }
                style={{ marginBottom: 10 }}
              >
                <Select
                  mode="multiple"
                  allowClear
                  placeholder={vehicleTypeIdsWatch?.length ? '选择子车型（可多选）' : '请先选择车型'}
                  disabled={!vehicleTypeIdsWatch?.length || !!vehicleTypesError}
                  options={vehicleSubtypeOptions}
                  filterOption={(input, option) =>
                    String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                />
              </Form.Item>
            </Col>
          )}
          <Col span={8}>
            <Form.Item
              name="name"
              label="项目名称"
              rules={[{ required: true, message: '请输入项目名称' }]}
              validateStatus={nameWarning ? 'warning' : undefined}
              help={nameWarning || undefined}
              style={{ marginBottom: 10 }}
            >
              <Input
                placeholder="输入车型名称"
                onChange={(e) => {
                  const val = e.target.value.trim();
                  const others = projects.filter((p) => !project || p.id !== project.id);
                  setNameWarning(others.some((p) => p.name === val) ? `项目名称"${val}"已存在` : '');
                }}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="code" label="项目代码" tooltip="项目的唯一标志ID" rules={[{ required: true, message: '请输入项目代码' }]} style={{ marginBottom: 10 }}>
              <Input
                disabled={!!project}
                placeholder="根据所属山头和项目名称自动生成"
                onChange={() => { codeManuallyEdited.current = true; }}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Row 3: 基础项目 + 域控形态 + MCU型号 */}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="基础项目" tooltip="可选择主线，也可以选择某一项目" style={{ marginBottom: 10 }}>
              <Cascader
                disabled={!!project}
                placeholder="选择基础项目（可选）"
                options={allProjectsCascaderOptions}
                value={baseCascaderVal}
                onChange={(v) => {
                  const id = v?.length ? parseInt(v[v.length - 1], 10) : undefined;
                  form.setFieldValue('baseProjectId', id);
                  setSelectedBaseProjectId(id);
                  handleBaseProjectChange(id);
                }}
                showSearch={{ filter: projectCascaderFilter }}
                allowClear
                expandTrigger="click"
                displayRender={labels => labels?.[labels.length - 1] || ''}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="adcu_model" label="域控形态" rules={[{ required: true, message: '请选择域控形态' }]} style={{ marginBottom: 10 }}>
              <CreatableSelect options={fieldOptions.adcuModels} placeholder="选择或输入域控形态" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="mcu_model" label="MCU型号" rules={[{ required: true, message: '请选择MCU型号' }]} style={{ marginBottom: 10 }}>
              <CreatableSelect options={fieldOptions.mcuModels} placeholder="选择或输入MCU型号" />
            </Form.Item>
          </Col>
        </Row>

        {/* Row 4: 硬件平台 + SoC操作系统 + SoC中间件 */}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="platform" label="硬件平台" rules={[{ required: true, message: '请选择硬件平台' }]} style={{ marginBottom: 10 }}>
              <CreatableSelect options={fieldOptions.platforms} placeholder="选择或输入硬件平台" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="os" label="SoC操作系统" rules={[{ required: true, message: '请选择SoC操作系统' }]} style={{ marginBottom: 10 }}>
              <CreatableSelect options={fieldOptions.oses} placeholder="选择或输入SoC操作系统" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="soc_middleware" label="SoC中间件" rules={[{ required: true, message: '请选择SoC中间件' }]} style={{ marginBottom: 10 }}>
              <CreatableSelect options={fieldOptions.socMiddlewares} placeholder="选择或输入SoC中间件" />
            </Form.Item>
          </Col>
        </Row>

        {/* Row 5: MCU中间件 + 描述 */}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="mcu_middleware" label="MCU中间件" style={{ marginBottom: 10 }}>
              <CreatableSelect options={fieldOptions.mcuMiddlewares} placeholder="选择或输入（可选）" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="description" label="描述" style={{ marginBottom: 10 }}>
              <Input.TextArea rows={1} placeholder="选填，用户自定义" />
            </Form.Item>
          </Col>
        </Row>

        <Divider titlePlacement="start" style={{ margin: '2px 0 10px' }}>项目空间内容</Divider>

        {/* Row 5: 需求 + OPL + 设计材料 */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="req_mode"
              label={
                <span>
                  需求&nbsp;
                  <Tooltip title="独立表示基于基础项目单独复制一份需求，适用于新项目；共用表示和基础项目共用同一份需求，适用于复用项目；不创建需求适用于不释放需求只用于接收释放底软包的项目">
                    <QuestionCircleOutlined style={{ color: '#8c8c8c', cursor: 'help' }} />
                  </Tooltip>
                </span>
              }
              rules={[{ required: true, message: '请选择需求模式' }]}
              style={{ marginBottom: 6 }}
            >
              <Radio.Group disabled={!!project || !!isMainline}>
                <Radio value="independent">独立</Radio>
                <Radio value="shared">共用</Radio>
                <Radio value="none">不创建需求</Radio>
              </Radio.Group>
            </Form.Item>
            {reqMode === 'shared' && (
              <Form.Item noStyle shouldUpdate={(prev, cur) => prev.baseProjectId !== cur.baseProjectId}>
                {() =>
                  !form.getFieldValue('baseProjectId') && (
                    <div style={{ color: '#faad14', marginBottom: 8, fontSize: 13 }}>
                      请在上方"基础项目"中选择要共用需求的项目
                    </div>
                  )
                }
              </Form.Item>
            )}
          </Col>
          <Col span={6}>
            <Form.Item name="opl_required" label="OPL" rules={[{ required: true, message: '请选择' }]} style={{ marginBottom: 6 }}>
              <Radio.Group disabled={!!isMainline} options={[{ label: '显示', value: true }, { label: '不显示', value: false }]} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="design_material_required" label="设计材料" rules={[{ required: true, message: '请选择' }]} style={{ marginBottom: 6 }}>
              <Radio.Group disabled={!!isMainline} options={[{ label: '显示', value: true }, { label: '不显示', value: false }]} />
            </Form.Item>
          </Col>
        </Row>

        {/* Row 6: 底软供应商文件类型 + Momenta文件类型 */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="allowed_file_type_ids" label="底软供应商文件类型" tooltip="可拖动调整显示顺序" style={{ marginBottom: 10 }}>
              <SortableFileTypeSelect allFileTypes={fileTypes} disabled={!!isMainline} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="momenta_file_type_ids" label="Momenta释放文件类型" tooltip="可拖动调整显示顺序" style={{ marginBottom: 10 }}>
              <SortableFileTypeSelect allFileTypes={fileTypes} disabled={!!isMainline} />
            </Form.Item>
          </Col>
        </Row>

        {/* Row 6b: 上传第三方 + 底软供应商 + RPA车型交互形式 */}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="third_party_plugin_id"
              label="上传第三方"
              tooltip="选择后，该项目的 Momenta 释放产物包可上传到对应第三方平台；留空表示不启用"
              style={{ marginBottom: 10 }}
            >
              <Select
                allowClear
                placeholder="不启用"
                options={thirdPartyPlugins.map((p) => ({ label: p.name, value: p.id }))}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="software_supplier_ids"
              label="底软供应商"
              tooltip="点击勾选底软供应商，可现场新建；一个项目可挂多家"
              style={{ marginBottom: 10 }}
            >
              <SoftwareSupplierSelect
                suppliers={softwareSuppliers}
                onCreated={(created) => setSoftwareSuppliers((prev) => [...prev, created])}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="rpa_vehicle_mode"
              label="RPA车型交互形式"
              tooltip="组合串：RPA 查询/发布按「车型/子车型/硬件平台」交互；项目名：按项目名交互，发布时释放包填入项目全部车型/子车型"
              style={{ marginBottom: 10 }}
            >
              <Select
                options={[
                  { value: 'combo', label: '车型/子车型/硬件平台' },
                  { value: 'project_name', label: '项目名' },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider titlePlacement="start" style={{ margin: '2px 0 10px' }}>人员配置</Divider>

        {/* Row 7: 负责人 + 交付物上传通知人 + CCB Reviewer（RPA Key 仅在山头未配置时补位） */}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="owner_id" label="负责人" style={{ marginBottom: 10 }}>
              <LdapUserSelect mode="single" placeholder="输入名字搜索" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="notify_user_ids" label="交付物上传通知人" tooltip="底软包发布变更时将通知这些人员" style={{ marginBottom: 10 }}>
              <LdapUserSelect mode="multiple" placeholder="输入名字搜索" disabled={!!isMainline} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="rpa_ccb_reviewers" label="CCB Reviewer" tooltip="飞书项目 CCB Reviewer 角色用户。留空则回退使用项目通知用户" style={{ marginBottom: 10 }}>
              <LdapUserSelect mode="multiple" placeholder="输入名字搜索" />
            </Form.Item>
          </Col>
          {!mountainHasRpaKey && (
            <Col span={8}>
              <Form.Item
                name="rpa_project_key"
                label="RPA项目空间Key"
                tooltip="所属山头未确定 RPA 项目空间时，可在此从 RPA 列表选择或手动填写 project_key；山头一旦配置 Key，将自动继承并隐藏此项"
                style={{ marginBottom: 10 }}
              >
                <AutoComplete
                  options={rpaKeyOptions}
                  placeholder="从 RPA 列表选择，或手动填写 project_key"
                  allowClear
                  filterOption={(input, option) =>
                    String((option as { searchText?: string } | undefined)?.searchText ?? '')
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
          )}
        </Row>

        {/* Row 8: 需求变更通知人 + 需求修改权限用户 */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="requirement_change_notify_user_ids" label="需求变更通知人" tooltip="需求发生变更时，将通过邮件通知这些人员" style={{ marginBottom: 10 }}>
              <LdapUserSelect mode="multiple" placeholder="输入名字搜索" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="requirement_change_user_ids" label="需求修改权限用户" tooltip="只有列表中的用户才能更改该项目的需求；列表为空时不限制" style={{ marginBottom: 10 }}>
              <LdapUserSelect mode="multiple" placeholder="输入名字搜索" />
            </Form.Item>
          </Col>
        </Row>

      </Form>
    </Modal>
  );
};

export default ProjectFormModal;
